/**
 * GHL Qualifier — Location List page (`/admin/ghl-qualifier`).
 *
 * Landing page for the new qualifier app's admin dashboard. Mirrors the
 * existing GHL Middleware location list at /admin/ghl-middleware so operators
 * have a familiar shape, but pulls data from the qualifier-specific RPCs.
 *
 * Filtering model:
 *   - Global text `searchTerm` matches against location name + ID
 *   - Per-column filters (`columnFilters`) collapse each numeric column to
 *     'any' / 'has' (> 0) / 'none' (= 0) and the activity column to
 *     'any' / 'last_24h' / 'last_7d' / 'stale_30d'
 *   - `sort` controls which column the table is sorted by + direction
 *
 * All filter/sort logic runs in a single memo so the table receives
 * already-filtered, already-sorted data and stays presentation-only.
 */

import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { TimeRangeFilter } from '@/components/ghl-dashboard'
import { useUrlTimeRange } from '@/hooks/useUrlTimeRange'
import {
  useQualifierLocationOverview,
  useQualifierStats,
} from '@/hooks/useGHLQualifierDashboard'
import { QualifierStatCards } from '@/components/ghl-qualifier/QualifierStatCards'
import {
  QualifierLocationsTable,
  type LocationsTableFilters,
  type LocationsTableSort,
  type LocationsTableSortColumn,
} from '@/components/ghl-qualifier/QualifierLocationsTable'
import { QualifierLocationsCards } from '@/components/ghl-qualifier/QualifierLocationsCards'
import {
  MapPin,
  Search,
  FilterX,
  RotateCcw,
  AlertTriangle,
  DatabaseBackup,
  LayoutGrid,
  Table as TableIcon,
  Upload,
} from 'lucide-react'
import { FailedJobsPanel } from '@/components/ghl-qualifier/FailedJobsPanel'
import { BackfillPanel } from '@/components/ghl-qualifier/BackfillPanel'
import { BulkGoogleAdsUploadDialog } from '@/components/ghl-qualifier/BulkGoogleAdsUploadDialog'
import { QUALIFIER_DETAIL_DEFAULT_TAB } from '@/lib/ghl-qualifier'

type ViewMode = 'cards' | 'table'
const VIEW_MODE_STORAGE_KEY = 'ghl-qualifier-locations-view-mode'

const DEFAULT_FILTERS: LocationsTableFilters = {
  qualified: 'any',
  converted: 'any',
  activity: 'any',
}

const DEFAULT_SORT: LocationsTableSort = {
  by: 'activity',
  dir: 'desc',
}

const MS_PER_HOUR = 60 * 60 * 1000
const MS_PER_DAY = 24 * MS_PER_HOUR

export default function GHLQualifierLocationListPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [timeRange, setTimeRange] = useUrlTimeRange()
  const [searchTerm, setSearchTerm] = useState('')
  const [columnFilters, setColumnFilters] = useState<LocationsTableFilters>(DEFAULT_FILTERS)
  const [sort, setSort] = useState<LocationsTableSort>(DEFAULT_SORT)
  const [failedJobsPanelOpen, setFailedJobsPanelOpen] = useState(false)
  const [backfillPanelOpen, setBackfillPanelOpen] = useState(false)
  const [uploadOpen, setUploadOpen] = useState(false)

  // View toggle: cards vs table. Persisted to localStorage so the user's
  // preference survives reloads. Initializer runs only on first mount.
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (typeof window === 'undefined') return 'cards'
    const stored = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY)
    return stored === 'table' ? 'table' : 'cards'
  })

  // Cards-view pagination index (0-based). Reset whenever the filtered set
  // changes so operators don't land on an empty page after a filter narrows
  // the result count.
  const [cardsPage, setCardsPage] = useState(0)

  function handleViewChange(mode: string) {
    const next: ViewMode = mode === 'table' ? 'table' : 'cards'
    setViewMode(next)
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, next)
    }
  }

  const overview = useQualifierLocationOverview(timeRange)
  const stats = useQualifierStats(timeRange, null)

  // Memoize so the empty-array fallback doesn't flap the reference identity
  // every render (would force `filteredLocations` to recompute when nothing
  // actually changed). React Query caches the data array between fetches,
  // so the only churn comes from the `?? []` branch.
  const allLocations = useMemo(() => overview.data ?? [], [overview.data])

  const filteredLocations = useMemo(() => {
    let result: readonly typeof allLocations[number][] = allLocations

    // ─── Text search across name + id ────────────────────────────────────
    const term = searchTerm.trim().toLowerCase()
    if (term) {
      result = result.filter(
        (loc) =>
          loc.location_name.toLowerCase().includes(term) ||
          loc.location_id.toLowerCase().includes(term)
      )
    }

    // ─── Per-column has/none filters ─────────────────────────────────────
    if (columnFilters.qualified === 'has') {
      result = result.filter((l) => l.qualified_total > 0)
    } else if (columnFilters.qualified === 'none') {
      result = result.filter((l) => l.qualified_total === 0)
    }

    if (columnFilters.converted === 'has') {
      result = result.filter((l) => l.converted_completed > 0)
    } else if (columnFilters.converted === 'none') {
      result = result.filter((l) => l.converted_completed === 0)
    }

    // ─── Activity recency filter ─────────────────────────────────────────
    if (columnFilters.activity !== 'any') {
      const nowMs = Date.now()
      result = result.filter((l) => {
        if (!l.last_activity_at) {
          // "Never had activity" only matches stale_30d explicitly so it
          // doesn't disappear from last_24h/last_7d filtering.
          return columnFilters.activity === 'stale_30d'
        }
        const ageMs = nowMs - new Date(l.last_activity_at).getTime()
        switch (columnFilters.activity) {
          case 'last_24h':
            return ageMs <= MS_PER_DAY
          case 'last_7d':
            return ageMs <= 7 * MS_PER_DAY
          case 'stale_30d':
            return ageMs > 30 * MS_PER_DAY
          default:
            return true
        }
      })
    }

    // ─── Sort ────────────────────────────────────────────────────────────
    // Copy before sorting — never mutate the React Query cached array.
    const sorted = [...result].sort((a, b) => {
      let cmp = 0
      switch (sort.by) {
        case 'location':
          cmp = a.location_name.localeCompare(b.location_name)
          break
        case 'qualified':
          cmp = a.qualified_total - b.qualified_total
          break
        case 'converted':
          cmp = a.converted_completed - b.converted_completed
          break
        case 'activity': {
          const aT = a.last_activity_at ? new Date(a.last_activity_at).getTime() : 0
          const bT = b.last_activity_at ? new Date(b.last_activity_at).getTime() : 0
          cmp = aT - bT
          break
        }
      }
      return sort.dir === 'asc' ? cmp : -cmp
    })
    return sorted
  }, [allLocations, searchTerm, columnFilters, sort])

  const isEmpty = !overview.isLoading && allLocations.length === 0
  const isFilteredEmpty =
    !overview.isLoading && allLocations.length > 0 && filteredLocations.length === 0

  // Reset Cards pagination whenever the filtered count changes so the user
  // never gets stranded on an empty page after narrowing a filter.
  useEffect(() => {
    setCardsPage(0)
  }, [filteredLocations.length])

  const hasActiveFilters =
    searchTerm.trim().length > 0 ||
    columnFilters.qualified !== 'any' ||
    columnFilters.converted !== 'any' ||
    columnFilters.activity !== 'any'

  function clearAllFilters() {
    setSearchTerm('')
    setColumnFilters(DEFAULT_FILTERS)
  }

  function handleSort(column: LocationsTableSortColumn) {
    setSort((current) => {
      if (current.by === column) {
        // Clicking the same column toggles direction
        return { by: column, dir: current.dir === 'asc' ? 'desc' : 'asc' }
      }
      // Switching column defaults to descending — operators usually want
      // "highest first" when changing what they sort by.
      return { by: column, dir: 'desc' }
    })
  }

  function navigateToLocation(locationId: string) {
    navigate({
      pathname: `/admin/ghl-qualifier/location/${locationId}/${QUALIFIER_DETAIL_DEFAULT_TAB}`,
      search: location.search,
    })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">GHL Qualifier</h1>
          <p className="text-sm text-muted-foreground">
            Lead qualification + converted-lead lifecycle pipeline — select a location
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setUploadOpen(true)}
            className="gap-1.5"
            aria-label="Bulk-enable Google Ads flow from a CSV"
          >
            <Upload className="h-3.5 w-3.5" />
            Upload CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setFailedJobsPanelOpen(true)}
            className="gap-1.5"
            aria-label="View failed jobs across all locations"
          >
            <AlertTriangle className="h-3.5 w-3.5 text-red-600" />
            Failed jobs
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setBackfillPanelOpen(true)}
            className="gap-1.5"
            aria-label="Backfill missed Google Ads conversion uploads"
          >
            <DatabaseBackup className="h-3.5 w-3.5 text-amber-600" />
            Backfill
          </Button>
          <TimeRangeFilter value={timeRange} onChange={setTimeRange} />
        </div>
      </div>

      {/*
        Slide-in panel listing every failed qualified-lead or converted-lead job
        across all locations. Click a row to drill into its detail page.
      */}
      <FailedJobsPanel
        open={failedJobsPanelOpen}
        onOpenChange={setFailedJobsPanelOpen}
      />

      {/*
        Slide-in panel for backfilling missed Google Ads conversion uploads —
        converted contacts that completed before their location was switched to
        google_ads delivery. Accounts drop off the list once fully uploaded.
      */}
      <BackfillPanel open={backfillPanelOpen} onOpenChange={setBackfillPanelOpen} />

      {/* Bulk CSV upload → enables the direct Google Ads flow for many locations. */}
      <BulkGoogleAdsUploadDialog open={uploadOpen} onOpenChange={setUploadOpen} />

      {/* Header stat cards (rollup across all locations) */}
      <QualifierStatCards
        mode="overview"
        stats={stats.data}
        isLoading={stats.isLoading}
      />

      {/* Search + clear-all toolbar */}
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">Locations</h2>
        <div className="flex items-center gap-2">
          {hasActiveFilters ? (
            <Button
              variant="outline"
              size="sm"
              onClick={clearAllFilters}
              className="gap-1.5"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Clear filters
            </Button>
          ) : null}
          <div className="relative w-72">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by name or ID"
              className="pl-9"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search locations"
            />
          </div>
        </div>
      </div>

      {/* View toggle: Cards | Table. Hidden when there's no data so the
          choice doesn't sit above an empty state. */}
      {(!isEmpty && !isFilteredEmpty) && (
        <div className="flex items-center justify-end">
          <Tabs value={viewMode} onValueChange={handleViewChange}>
            <TabsList>
              <TabsTrigger value="cards" className="gap-1.5">
                <LayoutGrid className="h-3.5 w-3.5" />
                Cards
              </TabsTrigger>
              <TabsTrigger value="table" className="gap-1.5">
                <TableIcon className="h-3.5 w-3.5" />
                Table
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      )}

      {/* Empty state */}
      {isEmpty ? (
        <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
          <MapPin className="h-12 w-12 mb-4 opacity-30" />
          <p className="text-lg font-medium">No locations have processed contacts yet</p>
          <p className="text-sm mt-1">
            Install the GHL Qualifier app on a location to start processing webhooks.
          </p>
        </div>
      ) : isFilteredEmpty ? (
        <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
          <FilterX className="h-12 w-12 mb-4 opacity-30" />
          <p className="text-lg font-medium">No locations match your filters</p>
          <p className="text-sm mt-1 mb-4">
            {allLocations.length} locations are hidden by the current search and filters.
          </p>
          <button
            type="button"
            onClick={clearAllFilters}
            className="text-sm font-medium text-primary hover:underline"
          >
            Clear all filters
          </button>
        </div>
      ) : viewMode === 'cards' ? (
        <QualifierLocationsCards
          data={filteredLocations}
          isLoading={overview.isLoading}
          onCardClick={navigateToLocation}
          page={cardsPage}
          onPageChange={setCardsPage}
        />
      ) : (
        <QualifierLocationsTable
          data={filteredLocations}
          isLoading={overview.isLoading}
          onRowClick={navigateToLocation}
          filters={columnFilters}
          onFiltersChange={setColumnFilters}
          sort={sort}
          onSort={handleSort}
        />
      )}
    </div>
  )
}
